import { PlatformLocation } from '@angular/common';
import { Inject, Injectable } from '@angular/core';

import { OfficeHomePageErrorHandler } from '../error-handler/error-handler.service';
import { InstrumentationParam, InstrumentationService, KnownInstrumentationEvents } from '../shared/instrumentation/instrumentation.service';
import { FetchStrategy, InstrumentedHttp, InstrumentedHttpPromise } from '../shared/instrumentation/instrumented-http.service';
import { FileType } from '../shared/models/file-item.model';
import { HttpRequestOptions } from '../shared/models/http-request-options.model';
import { AadAppId, AAD_APP_ID } from '../shared/services/aad-app-id.injection-token';
import { AccessTokenConsumer, AccessTokenService } from '../shared/services/access-token.service';
import { EdgeworthConfigService } from '../shared/services/edgeworth-config.service';
import { OfficeHomeApiConfigService } from '../shared/services/officehome-api-config.service';
import { SchedulerService } from '../shared/services/scheduler.service';
import { XhrService } from '../shared/services/xhr.service';
import { ClientStartupConfig, ClientStartupConfigService } from '../shared/shared-context/client-startup-config.service';
import { ContextService } from '../shared/shared-context/context.service';
import { GuidGenerator } from '../shared/utilities/guid-generator.utility';
import { clientRequestIdHeader, EdgeworthSubstrateRecentData, EdgeworthSubstrateRecentResponseData, localeParam, RawEdgeworthSubstrateRecentResponseData } from './edgeworth-data.model';
import { createRequestConfig, createS2SRequestConfig, getServerDebugInfoString, processUserRelationship, safeParseDate } from './edgeworth-data.utility';

@Injectable({
    providedIn: 'root'
})
export class EdgeworthSubstrateRecentService extends XhrService {
    private readonly endpoint: string;
    private fetchRequestIndex = 0;
    private cachedResponse: Promise<EdgeworthSubstrateRecentData>;
    private readonly startupConfig: ClientStartupConfig;

    constructor(
        accessTokenService: AccessTokenService,
        contextService: ContextService,
        instrumentationService: InstrumentationService,
        instrumentedHttp: InstrumentedHttp,
        officeHomeApiConfigService: OfficeHomeApiConfigService,
        platformLocation: PlatformLocation,
        private readonly globalErrorHandler: OfficeHomePageErrorHandler,
        startupConfigService: ClientStartupConfigService,
        scheduler: SchedulerService,
        @Inject(AAD_APP_ID) private readonly aadAppId: AadAppId,
        private readonly edgeworthConfigService: EdgeworthConfigService
    ) {
        super(
            accessTokenService,
            instrumentationService,
            instrumentedHttp,
            AccessTokenConsumer.Substrate,
            contextService,
            officeHomeApiConfigService,
            platformLocation,
            scheduler
        );
        this.startupConfig = startupConfigService.config;
        const config = this.edgeworthConfigService.config;

        if (config) {
            const queryParams = `top=${config.recentItemsCount}&${localeParam}${this.contextService.sharedContext.officeMarket}`;
            if (config.edgeworthS2SRecentEnabled) {
                this.endpoint = `${contextService.sharedContext.officeHomeApiRoot}/api/ew/recent?${queryParams}`;
            } else {
                this.endpoint = `${config.substrateApiEndpoint}${config.edgeworthApiRootPath}${config.recentApiPath}?${queryParams}`;
            }

            if (config.userActionSortOrderEnabled) {
                this.endpoint += '&order=UserActions';
            }
        }
    }

    public fetchEdgeworthSubstrateRecent(appFilter: ReadonlySet<FileType>): Promise<EdgeworthSubstrateRecentData> {
        if (this.cachedResponse) {
            return this.cachedResponse;
        }

        this.cachedResponse = new Promise<EdgeworthSubstrateRecentData>((resolve, reject) => {
            const requestId = GuidGenerator.createGuid();
            const requestConfig = this.edgeworthConfigService.config.edgeworthS2SRecentEnabled ?
             createS2SRequestConfig(requestId) :
             createRequestConfig(
                `${this.startupConfig.objectId}@${this.startupConfig.tenantId}`,
                this.contextService.sharedContext.recommendedIceApplicationId.toString(),
                this.startupConfig.objectId,
                this.aadAppId,
                requestId,
                this.contextService.getCurrentPageForLogging());
            const promiseCallback = (promise: InstrumentedHttpPromise<RawEdgeworthSubstrateRecentResponseData>) => {
                promise
                    .instrument((result, event) => {
                        event.params[InstrumentationParam.ActualSize] = (result?.data?.files?.length || 0).toString();
                        event.params[InstrumentationParam.RequestId] = this.getRequestIdFromResponse(result);
                        event.params[InstrumentationParam.DebugInfo] = getServerDebugInfoString(result);
                        event.params[InstrumentationParam.Index] = (this.fetchRequestIndex++).toString();
                        event.params[InstrumentationParam.ImpressionId] = (!!result.headers && result.headers.get(clientRequestIdHeader)) || requestId;
                    })
                    .then(result => {
                        resolve({
                            data: this.parse(result.data),
                            impressionId: !!result.headers && result.headers.get(clientRequestIdHeader) || requestId
                        });
                    })
                    .catch(error => {
                        this.globalErrorHandler.handleError(error);
                    });
            };

            const appFilterInputValues = appFilter ? Array.from(appFilter.values()) : [];
            const recentApiAppParams = new Set<string>();

            appFilterInputValues.forEach(param => {
                const recentApiAppParam = this.getRecentAppParam(param);

                if (!param) {
                    return;
                }

                recentApiAppParams.add(recentApiAppParam);
            });

            const appFilterValues = recentApiAppParams.size ? Array.from(recentApiAppParams.values()).join(',') : '';

            const endpoint = appFilterValues ? `${this.endpoint}&apps=${appFilterValues}` : this.endpoint;

            this.get<RawEdgeworthSubstrateRecentResponseData>(
                KnownInstrumentationEvents.EdgeworthSubstrateRecentRequest,
                endpoint,
                promiseCallback,
                reject,
                requestConfig,
                FetchStrategy.NetworkOnly,
                null,
                null,
                null,
                this.edgeworthConfigService.config.edgeworthS2SRecentEnabled ? AccessTokenConsumer.OfficeHome : AccessTokenConsumer.Substrate
            );
        });

        return this.cachedResponse;
    }

    protected handleQueryParam(paramNameInLowerCase: string, value: string | Array<string>, requestConfig: HttpRequestOptions) {
        if (paramNameInLowerCase === 'mockmru' && this.mockRequestHeader) {
            requestConfig.headers.set(this.mockRequestHeader, value);
        }
    }

    private parse(data: RawEdgeworthSubstrateRecentResponseData): EdgeworthSubstrateRecentResponseData {
        const files = data.files.map(file => {
            const userRelationship = file.user_relationship;
            const activityBadge = file.activity_badge ? {
                ...file.activity_badge,
                timestamp: safeParseDate(file.activity_badge.timestamp)
            } : undefined;

            return {
                ...file,
                last_recommended_modified_datetime: file.last_recommended_modified_datetime,
                last_store_modified_datetime: file.last_store_modified_datetime,
                user_relationship: processUserRelationship(userRelationship),
                created_datetime: safeParseDate(file.created_datetime),
                creation_info: file.creation_info ? {
                    ...file.creation_info,
                    timestamp: safeParseDate(file.creation_info.timestamp)
                } : null,
                activity_badge: activityBadge,
                indexed: false,
                server_last_recommended_modified_datetime: file.last_recommended_modified_datetime,
                server_last_store_modified_datetime: file.last_recommended_modified_datetime
            };
        });

        return {
            diagnostics_data: data.diagnostics_data,
            files,
            is_complete: data.is_complete
        };
    }

    // the Recent API only supports specific app types, others fails the request with 400 - Bad Request
    // List of allowed types can be found here: https://o365exchange.visualstudio.com/O365%20Core/_git/Griffin?path=%2Fsources%2Fdev%2FEdgeworth%2Fdocs%2FRecent.md&_a=preview&version=GBmaster
    private getRecentAppParam(fileType: FileType) {
        switch (fileType) {
            case FileType.Word:
                return 'Word';
            case FileType.PowerPoint:
                return 'PowerPoint';
            case FileType.Excel:
                return 'Excel';
            case FileType.Video:
            case FileType.Stream:
            case FileType.StreamsVideo:
                return 'Video';
            case FileType.Visio:
            case FileType.VisioSoftwareWac:
            case FileType.VisioBusinessWac:
            case FileType.VisioFlowChartWac:
            case FileType.VisioGeneralWac:
            case FileType.VisioNetworkWac:
            case FileType.VisioStartWac:
                return 'Visio';
            default:
                return null;
        }
    }
}
